import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { ActivityMediaRenderer } from './ActivityMediaRenderer';
import { useStageTokens } from '../hooks/useStageTokens';

interface CopyWorkActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * CopyWorkActivity — split-screen copy mechanic.
 *
 * Top half: The model text displayed in a clear, large, easy-to-read font.
 *           A thin divider separates it from the working area.
 * Bottom half: Skia handwriting canvas where the student copies the text.
 *
 * Supporting media (e.g. an audio reading aloud the text) can be
 * attached and will render above the model text via ActivityMediaRenderer.
 */
export const CopyWorkActivity: React.FC<CopyWorkActivityProps> = ({ activity, onComplete, keyStage }) => {
    const targetText: string = activity.config?.targetText || '';
    const media = activity.media || [];
    const tokens = useStageTokens(keyStage);

    const { completedStrokes, clearStrokes } = useStrokeStore();

    return (
        <View style={styles.container}>
            {/* ── Top half: model text ─────────────────────────── */}
            <View style={styles.topHalf}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                    </View>
                    {activity.instructions && (
                        <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                    )}
                </View>

                {/* Optional media (e.g. audio pronunciation) */}
                <ActivityMediaRenderer media={media} placement="before" />

                {/* The model text the student copies */}
                <View style={styles.modelBox}>
                    <Text style={styles.modelLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Model text — copy below:</Text>
                    <Text testID="copy-model-text" style={styles.modelText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{targetText}</Text>
                </View>
            </View>

            {/* Divider */}
            <View style={styles.divider}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Your copy ↓</Text>
                <View style={styles.dividerLine} />
            </View>

            {/* ── Bottom half: canvas with ruled guidelines ───────────────────── */}
            <View testID="copy-canvas-area" style={styles.canvasArea}
                accessibilityRole="image"
                accessibilityLabel="Handwriting canvas for copying the model text"
            >
                {/* Lined paper guidelines for neat handwriting */}
                <View style={styles.guidelinesContainer} pointerEvents="none">
                    {[0, 1, 2, 3].map((lineIdx) => (
                        <View key={lineIdx} style={styles.guidelineRow}>
                            <View style={styles.guidelineTop} />
                            <View style={styles.guidelineMid} />
                            <View style={styles.guidelineBase} />
                        </View>
                    ))}
                </View>
                <SkiaCanvas />
            </View>

            {/* Footer */}
            <View style={styles.footer}>
                <TouchableOpacity style={styles.clearBtn} onPress={clearStrokes}
                    accessibilityRole="button"
                    accessibilityLabel="Clear"
                    accessibilityHint="Clears all strokes from the canvas"
                >
                    <Text style={styles.clearTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.submitBtn, completedStrokes.length === 0 && styles.disabledBtn]}
                    onPress={onComplete}
                    disabled={completedStrokes.length === 0}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your copied work"
                    accessibilityState={{ disabled: completedStrokes.length === 0 }}
                >
                    <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>

            {/* Teacher feedback overlay */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} strokes={[]} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 16 },

    topHalf: { paddingHorizontal: 16, paddingVertical: 10 },
    header: { marginBottom: 8 },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 2 },
    title: { fontSize: 20, fontWeight: 'bold', color: '#1E293B' },
    instructions: { fontSize: 14, color: '#64748B', fontStyle: 'italic' },

    modelBox: {
        backgroundColor: '#F8FAFC', borderRadius: 12,
        borderWidth: 1, borderColor: '#E2E8F0',
        padding: 12,
    },
    modelLabel: {
        fontSize: 10, fontWeight: 'bold', color: '#94A3B8',
        letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4,
    },
    modelText: {
        fontSize: 20, lineHeight: 28, color: '#1E293B',
        fontWeight: '600', letterSpacing: 0.3,
    },

    divider: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: 16, paddingVertical: 6, gap: 8,
    },
    dividerLine: { flex: 1, height: 1, backgroundColor: '#CBD5E1' },
    dividerLabel: { fontSize: 11, color: '#94A3B8', fontWeight: '600' },

    canvasArea: { flex: 1, marginHorizontal: 16, marginBottom: 8, borderRadius: 12, overflow: 'hidden', backgroundColor: '#FAFAFA', position: 'relative' },
    guidelinesContainer: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'space-evenly',
        paddingVertical: 12,
        paddingHorizontal: 8,
    },
    guidelineRow: {
        height: 56,
        justifyContent: 'space-between',
        marginVertical: 4,
    },
    guidelineTop: { height: 1, backgroundColor: '#E2E8F0', opacity: 0.7 },
    guidelineMid: { height: 1, borderColor: '#CBD5E1', borderWidth: 0.5, borderStyle: 'dashed', opacity: 0.6 },
    guidelineBase: { height: 1.5, backgroundColor: '#94A3B8', opacity: 0.8 },

    footer: {
        flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 10, gap: 10,
        borderTopWidth: 1, borderTopColor: '#F1F5F9',
    },
    clearBtn: {
        flex: 1, height: 56, minHeight: 52, borderRadius: 12, borderWidth: 1, borderColor: '#CBD5E1',
        alignItems: 'center', justifyContent: 'center',
    },
    clearTxt: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 3,
        height: 56,
        minHeight: 52,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },
});
